import { useCallback, useMemo, useState } from 'react';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';
import { ToastContext } from './toast-context';

/** Accent bar, icon tile and title for each tone, on a solid white card. */
const TONES = {
  success: {
    bar: 'bg-success-500',
    tile: 'bg-success-100 text-success-700',
    icon: 'check',
    title: 'Done',
  },
  error: {
    bar: 'bg-danger-500',
    tile: 'bg-danger-100 text-danger-700',
    icon: 'alert',
    title: 'Something went wrong',
  },
  info: {
    bar: 'bg-brand-500',
    tile: 'bg-brand-100 text-brand-700',
    icon: 'info',
    title: 'Heads up',
  },
};

let nextId = 1;

/** Application-wide feedback for actions that succeed or fail. */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (message, tone = 'info') => {
      const id = nextId++;
      setToasts((current) => [...current, { id, tone, message }]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      notify,
      success: (message) => notify(message, 'success'),
      error: (message) => notify(message, 'error'),
    }),
    [notify],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Top right, just under the top bar, above dialogs: where the eye goes
          after clicking a button, and never under the taskbar or a footer. */}
      <div
        className="pointer-events-none fixed top-16 right-4 z-[100] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2.5 sm:right-6"
        aria-live="polite"
      >
        {toasts.map((toast) => {
          const tone = TONES[toast.tone] ?? TONES.info;
          return (
            <div
              key={toast.id}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className="toast-in border-line bg-surface pointer-events-auto relative overflow-hidden rounded-xl border shadow-[0_12px_32px_-8px_rgba(30,16,60,0.28)]"
            >
              <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', tone.bar)} />
              <div className="flex items-start gap-3 py-3 pr-3 pl-4">
                <span
                  className={cn('grid size-8 shrink-0 place-items-center rounded-full', tone.tile)}
                >
                  <Icon name={tone.icon} size={16} />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-ink text-sm font-semibold">{tone.title}</p>
                  <p className="text-ink-muted mt-0.5 text-sm leading-snug break-words">
                    {toast.message}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  className="text-ink-muted hover:bg-surface-muted hover:text-ink grid size-7 shrink-0 place-items-center rounded-md transition"
                  aria-label="Dismiss"
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
